import React, { useState, useEffect, useCallback } from 'react';
import { invites } from './api';
import { useAuth } from './AuthContext';
import { formatDateTime, relativeTime } from './format';
import './Portal.css';

const STATUS_PILL = {
  pending: 'warn',
  accepted: 'ok',
  expired: 'muted',
};

export default function OwnerAccessPanel() {
  const { user: signedInUser } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ invites: [], accounts: [] });
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [revoking, setRevoking] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [removing, setRemoving] = useState(null);

  const load = useCallback(async () => {
    try {
      const response = await invites.list();
      setData(response);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSend = async (event) => {
    event.preventDefault();
    setError(null);
    setResult(null);
    setCopied(false);
    setSending(true);
    try {
      const response = await invites.send(email.trim(), name.trim());
      setResult(response);
      setName('');
      setEmail('');
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleRevoke = async (id) => {
    setRevoking(id);
    setError(null);
    try {
      await invites.revoke(id);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setRevoking(null);
    }
  };

  const removeAccount = async (id) => {
    if (confirmRemove !== id) {
      setConfirmRemove(id);
      setTimeout(() => setConfirmRemove((current) => (current === id ? null : current)), 5000);
      return;
    }
    setConfirmRemove(null);
    setRemoving(id);
    setError(null);
    try {
      await invites.removeAccount(id);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setRemoving(null);
    }
  };

  const copyLink = () => {
    if (!result || !result.invite_url) return;
    const write = navigator.clipboard && navigator.clipboard.writeText
      ? navigator.clipboard.writeText(result.invite_url)
      : Promise.reject();
    write.then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      },
      () => {
        // Clipboard access is blocked on insecure origins and in some
        // browsers — the link is on screen either way, so just select it.
        const field = document.getElementById('pt-invite-url');
        if (field && field.select) field.select();
      }
    );
  };

  return (
    <div>
      <section className="pt-section">
        <h2 className="pt-section-title">Invite the business owner</h2>
        <p className="pt-page-sub" style={{ marginTop: 6 }}>
          Sends a one-time link that lets the owner choose their own password. You never see or set it.
          The link expires after 7 days and stops working once it has been used.
        </p>

        <div className="pt-card" style={{ maxWidth: 620 }}>
          <form className="pt-form" onSubmit={handleSend}>
            <div className="pt-row">
              <div className="pt-field">
                <label className="pt-label" htmlFor="pt-invite-name">
                  Owner's name
                </label>
                <input
                  id="pt-invite-name"
                  className="pt-input"
                  type="text"
                  placeholder="e.g. Dave"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div className="pt-field">
                <label className="pt-label" htmlFor="pt-invite-email">
                  Owner's email *
                </label>
                <input
                  id="pt-invite-email"
                  className="pt-input"
                  type="email"
                  placeholder="owner@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>

            {error && <div className="pt-notice pt-notice-error">{error}</div>}

            {result && (
              <div className={'pt-notice pt-notice-' + (result.emailed ? 'success' : 'warn')}>
                <div style={{ marginBottom: result.emailed ? 0 : 10 }}>{result.message}</div>
                {!result.emailed && (
                  <>
                    <input
                      id="pt-invite-url"
                      className="pt-input"
                      readOnly
                      value={result.invite_url}
                      onFocus={(e) => e.target.select()}
                      style={{ fontSize: '0.82rem', marginBottom: 8 }}
                    />
                    <button className="pt-btn pt-btn-ghost pt-btn-sm" type="button" onClick={copyLink}>
                      {copied ? 'Copied' : 'Copy link'}
                    </button>
                  </>
                )}
              </div>
            )}

            <div className="pt-actions">
              <button className="pt-btn pt-btn-primary" type="submit" disabled={sending}>
                {sending && <span className="pt-spinner" />}
                {sending ? 'Sending…' : 'Send invitation'}
              </button>
              <span className="pt-hint">The owner sets their own password from the link.</span>
            </div>
          </form>
        </div>
      </section>

      <section className="pt-section">
        <div className="pt-section-head">
          <h2 className="pt-section-title">Invitations</h2>
          <button className="pt-btn pt-btn-ghost pt-btn-sm" type="button" onClick={load}>
            Refresh
          </button>
        </div>

        <div className="pt-card">
          {loading ? (
            <div className="pt-hint">Loading…</div>
          ) : data.invites.length === 0 ? (
            <div className="pt-hint">No invitations have been sent yet.</div>
          ) : (
            <div className="pt-table-wrap">
              <table className="pt-table">
                <thead>
                  <tr>
                    <th>Recipient</th>
                    <th>Status</th>
                    <th>Sent</th>
                    <th>Expires</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.invites.map((invite) => (
                    <tr key={invite.id}>
                      <td>
                        <strong>{invite.name}</strong>
                        <div className="pt-hint">{invite.email}</div>
                      </td>
                      <td>
                        <span className={'pt-pill pt-pill-' + (STATUS_PILL[invite.status] || 'muted')}>
                          <span className="pt-dot" />
                          {invite.status}
                        </span>
                      </td>
                      <td className="pt-hint">{relativeTime(invite.created_at)}</td>
                      <td className="pt-hint">
                        {invite.status === 'accepted'
                          ? 'Used ' + relativeTime(invite.used_at)
                          : formatDateTime(invite.expires_at)}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {invite.status === 'pending' && (
                          <button
                            className="pt-btn pt-btn-danger pt-btn-sm"
                            type="button"
                            onClick={() => handleRevoke(invite.id)}
                            disabled={revoking === invite.id}
                          >
                            {revoking === invite.id ? 'Revoking…' : 'Revoke'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <section className="pt-section">
        <h2 className="pt-section-title">Accounts</h2>
        <div className="pt-card">
          {data.accounts.length === 0 ? (
            <div className="pt-hint">No accounts yet.</div>
          ) : (
            <div className="pt-table-wrap">
              <table className="pt-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Created</th>
                    <th>Last sign-in</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.accounts.map((account) => (
                    <tr key={account.id}>
                      <td>
                        <strong>{account.name}</strong>
                        <div className="pt-hint">{account.email}</div>
                      </td>
                      <td>
                        <span className={'pt-pill pt-pill-' + (account.role === 'webmaster' ? 'ok' : 'muted')}>
                          <span className="pt-dot" />
                          {account.role}
                        </span>
                        {account.password_is_temporary && (
                          <div className="pt-hint" style={{ marginTop: 4 }}>
                            issued password
                          </div>
                        )}
                      </td>
                      <td className="pt-hint">{formatDateTime(account.created_at)}</td>
                      <td className="pt-hint">
                        {account.last_login_at ? relativeTime(account.last_login_at) : 'Never'}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {signedInUser && account.id !== signedInUser.id && (
                          <button
                            className="pt-btn pt-btn-danger pt-btn-sm"
                            type="button"
                            onClick={() => removeAccount(account.id)}
                            disabled={removing === account.id}
                          >
                            {removing === account.id
                              ? 'Removing…'
                              : confirmRemove === account.id
                              ? 'Really remove?'
                              : 'Remove'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
